import { Save, Loader2 } from 'lucide-react';
import { ContractDetails } from '@/services/contractApi';
import { PlanTier } from '@/services/organizationApi';

export interface ContractTermsFormState {
  planSlug: string;
  teamQuantity: number;
  contractReference: string;
  contractSignedAt: string;
  contractStartsAt: string;
  contractEndsAt: string;
  contractNotes: string;
  reason: string;
}

interface Props {
  form: ContractTermsFormState;
  setForm: (form: ContractTermsFormState) => void;
  contract: ContractDetails | null;
  plans: PlanTier[];
  isDirty: boolean;
  saving: boolean;
  onSubmit: () => void;
  /**
   * Masque les champs plan/quota mono-plan — utile en création multi-plan où
   * le mix est géré par CreationPlansEditor (composant frère).
   */
  hidePlanFields?: boolean;
  /**
   * Liste lisible des champs requis non saisis. Affichée à côté du bouton
   * désactivé pour expliquer pourquoi le submit est bloqué.
   */
  missingFields?: string[];
}

/**
 * Formulaire des termes du contrat (référence, dates, notes, plan/quota mono-plan
 * pour la création initiale). Extrait de ContractPage.tsx pour rester sous le
 * seuil de 500 lignes (cf. review-checklist).
 *
 * La gestion multi-plan (tableau dynamique de plans) vit dans
 * ContractPlansEditor.tsx — ce formulaire ne gère que les termes contractuels
 * et le plan unique de création.
 */
export function ContractTermsForm({ form, setForm, contract, plans, isDirty, saving, onSubmit, hidePlanFields, missingFields }: Props) {
  // Classe d'un input date : si vide, texte gris (placeholder jj/mm/aaaa
  // visible et déteint) ; si rempli, texte sombre. Bordure ambrée quand un
  // champ requis est vide pour signaler la saisie manquante.
  const dateInputClass = (value: string, required: boolean) => {
    const filled = value.length > 0;
    const text = filled ? 'text-gray-900' : 'text-gray-400';
    const border = filled
      ? 'border-gray-300'
      : required
        ? 'border-amber-300 bg-amber-50/40'
        : 'border-gray-300 border-dashed';
    return `w-full px-3 py-2 border ${border} ${text} rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:text-gray-900`;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h2 className="text-sm font-semibold text-gray-900 mb-4">
        {contract ? 'Modifier les termes du contrat' : 'Mettre en place un contrat'}
      </h2>

      <div className="grid grid-cols-2 gap-4">
        {!hidePlanFields && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Plan *</label>
              <select
                value={form.planSlug}
                onChange={(e) => setForm({ ...form, planSlug: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              >
                {plans.map((p) => (
                  <option key={p.id} value={p.slug}>{p.name}</option>
                ))}
              </select>
              {contract && (
                <p className="mt-1 text-xs text-gray-500">
                  Changement de plan tracé dans l'audit (avant → après).
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Quota de sièges *</label>
              <input
                type="number"
                min={1}
                value={form.teamQuantity}
                onChange={(e) => setForm({ ...form, teamQuantity: parseInt(e.target.value, 10) || 1 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </>
        )}

        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Référence contrat *
            {!form.contractReference.trim() && (
              <span className="ml-1 text-amber-700 font-normal">— non saisie</span>
            )}
          </label>
          <input
            type="text"
            value={form.contractReference}
            onChange={(e) => setForm({ ...form, contractReference: e.target.value })}
            placeholder="ex. CONT-2026-001"
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 ${
              !form.contractReference.trim()
                ? 'border-amber-300 bg-amber-50/40'
                : 'border-gray-300'
            }`}
            aria-invalid={!form.contractReference.trim()}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Date de signature *
            {!form.contractSignedAt && (
              <span className="ml-1 text-amber-700 font-normal">— non saisie</span>
            )}
          </label>
          <input
            type="date"
            value={form.contractSignedAt}
            onChange={(e) => setForm({ ...form, contractSignedAt: e.target.value })}
            className={dateInputClass(form.contractSignedAt, true)}
            aria-invalid={!form.contractSignedAt}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Date de début
            {!form.contractStartsAt && (
              <span className="ml-1 text-gray-400 font-normal">— optionnel</span>
            )}
          </label>
          <input
            type="date"
            value={form.contractStartsAt}
            onChange={(e) => setForm({ ...form, contractStartsAt: e.target.value })}
            className={dateInputClass(form.contractStartsAt, false)}
          />
        </div>

        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Date de fin *
            {!form.contractEndsAt && (
              <span className="ml-1 text-amber-700 font-normal">— non saisie</span>
            )}
          </label>
          <input
            type="date"
            value={form.contractEndsAt}
            onChange={(e) => setForm({ ...form, contractEndsAt: e.target.value })}
            className={dateInputClass(form.contractEndsAt, true)}
            aria-invalid={!form.contractEndsAt}
          />
        </div>

        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={form.contractNotes}
            onChange={(e) => setForm({ ...form, contractNotes: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            placeholder="Conditions particulières, contacts commerciaux…"
          />
        </div>

        <div className="col-span-2 pt-2 border-t border-gray-100">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Raison de la modification (audit) *
            {form.reason.trim().length === 0 && (
              <span className="ml-1 text-amber-700 font-normal">— non saisie</span>
            )}
            {form.reason.trim().length > 0 && form.reason.trim().length < 3 && (
              <span className="ml-1 text-amber-700 font-normal">— min 3 caractères</span>
            )}
          </label>
          <input
            type="text"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="ex. Avenant signé le 15/05 — ajout équipe Marketing"
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 ${
              form.reason.trim().length < 3
                ? 'border-amber-300 bg-amber-50/40'
                : 'border-gray-300'
            }`}
            aria-invalid={form.reason.trim().length < 3}
          />
          <p className="mt-1 text-xs text-gray-500">
            Cette raison est enregistrée dans le journal d'audit avec votre identité, l'IP et l'horodatage.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 mt-4">
        {/* Diagnostic des champs manquants : affiché quand le bouton est gris
            pour expliquer pourquoi (raison oubliée, plan sous min_seats…). */}
        {!isDirty && missingFields && missingFields.length > 0 && (
          <p className="text-xs text-amber-700 flex-1">
            Manque : <span className="font-medium">{missingFields.join(', ')}</span>
          </p>
        )}
        {contract && !isDirty && (!missingFields || missingFields.length === 0) && (
          <p className="text-xs text-gray-500">Aucune modification à enregistrer</p>
        )}
        <button
          onClick={onSubmit}
          disabled={saving || !isDirty}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {contract ? 'Enregistrer les modifications' : 'Créer le contrat'}
        </button>
      </div>
    </div>
  );
}
